import {
  AlertTriangle,
  Check,
  FileCheck2,
  Lightbulb,
  LockKeyhole,
  MessageSquareText,
  Microscope,
  ShieldCheck,
  UserRoundCheck,
  Users,
} from 'lucide-react'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { ResearchIntakeForm } from '../components/ResearchIntakeForm'
import { Reveal } from '../components/Reveal'
import { SectionHeading } from '../components/SectionHeading'

const ideaPrompts = [
  { icon: MessageSquareText, title: 'A care barrier', copy: 'What made it harder to understand, access, or follow a health plan?' },
  { icon: Lightbulb, title: 'A useful idea', copy: 'What could make a clinic, app, instruction, or follow-up work better?' },
  { icon: Users, title: 'A recurring pattern', copy: 'What part of lived experience deserves a better research question?' },
]

const researchPath = [
  { number: '01', title: 'Private intake', copy: 'A focused story or idea arrives in the restricted Tally workspace, with one optional supporting document.' },
  { number: '02', title: 'Privacy review', copy: 'Text and any file are treated as potentially identifiable until a human privacy review.' },
  { number: '03', title: 'Study design & ethics', copy: 'A real research project needs a protocol, governance, and independent ethics review.' },
  { number: '04', title: 'Separate informed consent', copy: 'If invited, participation is optional and explained through a new, study-specific process.' },
  { number: '05', title: 'Publication permission', copy: 'Nothing identifiable is published from this form. Any later quotation or case story needs exact-material review and separate written consent.' },
]

export default function CommunityPage() {
  return (
    <>
      <PageMeta title="Community research ideas" description="Share a health experience, care barrier, research question, and one optional document through a private no-login form. Nothing is posted publicly." image="/images/research-participation.webp" imageAlt="Protected profile and paper-like information flowing into responsible research nodes" />
      <PageHero
        eyebrow="Community research ideas"
        title={<>Your experience can shape a <span className="serif-accent">better question.</span></>}
        copy="Share a focused experience, barrier, or idea for private review, with one optional supporting document. No account is required, contact details are optional, and nothing is posted publicly."
        tone="lime"
        aside={<div className="privacy-seal"><LockKeyhole size={28} /><strong>Restricted intake</strong><span>Text and uploaded files remain potentially identifiable until a human privacy review.</span></div>}
      />

      <ResearchIntakeForm />

      <section className="section community-participation-art">
        <div className="container participation-art-grid">
          <Reveal className="participation-art-figure">
            <img src="/images/research-participation.webp" width="1776" height="887" alt="Protected profile and paper-like information flowing into responsible research nodes" />
            <span className="participation-node participation-node-one" aria-hidden="true" />
            <span className="participation-node participation-node-two" aria-hidden="true" />
            <span className="participation-node participation-node-three" aria-hidden="true" />
            <small>AI-assisted concept art · not a data-flow diagram</small>
          </Reveal>
          <Reveal delay={0.08}>
            <span className="eyebrow"><Microscope size={15} /> From experience to evidence</span>
            <h2>A story can inspire research. It cannot <span className="serif-accent">skip research safeguards.</span></h2>
            <p>This first step helps identify a useful question. A future study still needs a protocol, eligibility rules, appropriate ethics review, and its own informed-consent process. Publication is a separate decision again.</p>
            <div className="participation-badges"><span><ShieldCheck size={17} /> No public feed</span><span><FileCheck2 size={17} /> One private file, if needed</span><span><UserRoundCheck size={17} /> Adults sharing their own experience</span></div>
          </Reveal>
        </div>
      </section>

      <section className="section community-intro-section">
        <div className="container">
          <Reveal><SectionHeading align="center" eyebrow="What belongs here" title={<>Share what matters. <span className="serif-accent">Limit what identifies.</span></>} copy="Keep text fields focused and remove unnecessary identifiers. If a supporting document is important, upload only your own and understand that hidden metadata or clinical details may still identify you." /></Reveal>
          <div className="idea-prompt-grid">{ideaPrompts.map((item, index) => <Reveal className="idea-prompt" key={item.title} delay={index * 0.06}><item.icon size={25} /><h3>{item.title}</h3><p>{item.copy}</p></Reveal>)}</div>
        </div>
      </section>

      <section className="section research-path-section">
        <div className="container">
          <Reveal><SectionHeading eyebrow="A responsible route" title={<>Five gates before anything could be <span className="serif-accent">published.</span></>} copy="Submitting text or a document only opens the first gate. It does not make someone a research participant, authorize broader record access, or grant permission to quote or publish." /></Reveal>
          <div className="research-path-grid">{researchPath.map((step, index) => <Reveal className="research-path-card" key={step.number} delay={(index % 5) * 0.04}><span>{step.number}</span><h3>{step.title}</h3><p>{step.copy}</p></Reveal>)}</div>
        </div>
      </section>

      <section className="section safety-rules-section">
        <div className="container safety-rules-grid">
          <Reveal className="safety-yes"><span className="rule-icon"><Check /></span><h2>Good to share</h2><ul><li>Your own focused experience or research question</li><li>A recurring access or communication barrier</li><li>An idea for education, care, or digital tools</li><li>One relevant PDF, JPG, or PNG about you, only if needed</li></ul></Reveal>
          <Reveal className="safety-no" delay={0.08}><span className="rule-icon"><AlertTriangle /></span><h2>Keep out of this form</h2><ul><li>Another person's or a minor's record or private story</li><li>Identity, insurance, payment, or legal documents</li><li>Executable, archived, password-protected, or active files</li><li>Anything needing urgent medical attention or a guaranteed reply</li></ul></Reveal>
        </div>
      </section>
    </>
  )
}
